import { Select, useSelectContext } from "@ark-ui/react";
import { mdiChevronDown } from "@mdi/js";
import Icon from "@mdi/react";
import { cva } from "class-variance-authority";
import clsx from "clsx";
import type { ComponentProps } from "react";

type ControlProps = ComponentProps<typeof Select.Control>;
type CustomProps<T> = {
  size?: "small" | "medium" | "large";
  variant?: "outline" | "underline" | "ghost";
  children?: React.ReactNode | ((props: { selectedItems: T[] }) => React.ReactNode);
};
type Props<T> = Omit<ControlProps, keyof CustomProps<T>> & CustomProps<T>;

const controlVariants = cva(
  "transition-colors focus-visible:outline-none text-gray-1000 inline-flex items-center w-full gap-1 relative",
  {
    variants: {
      variant: {
        outline: "border",
        underline: "border-b",
        ghost: "",
      },
      size: {
        small: "h-8 placeholder:text-label3 text-label3 px-2 pr-[calc(8px+14px)]",
        medium: "h-10 placeholder:text-label2 text-label2 px-3 pr-[calc(12px+16px)]",
        large: "h-12 placeholder:text-label1 text-label1 px-3 pr-[calc(16px+18px)]",
      },
      state: {
        default: "placeholder:text-gray-500",
        selected: "border-gray-500",
        opened: "border-gray-1000",
        disabled: "text-gray-300 bg-gray-100 border-gray-200 placeholder:text-gray-300",
      },
    },
    compoundVariants: [
      {
        variant: "outline",
        size: "small",
        class: "rounded-4",
      },
      {
        variant: "outline",
        size: "medium",
        class: "rounded-6",
      },
      {
        variant: "outline",
        size: "large",
        class: "rounded-8",
      },
      {
        variant: "outline",
        state: ["default", "opened", "selected"],
        class: "[&:is([data-invalid])]:border-red-500 [&:is([data-invalid])]:bg-red-10",
      },
      {
        variant: "underline",
        state: ["default", "opened", "selected"],
        class: "[&:is([data-invalid])]:border-b-red-500",
      },
      {
        variant: "outline",
        state: "disabled",
        class: "border-gray-300",
      },
      {
        variant: "underline",
        state: "disabled",
        class: "border-b-gray-300",
      },
      {
        variant: "outline",
        state: "default",
        class: "border-gray-500 hover:border-gray-600 focus:border-gray-1000",
      },
      {
        variant: "underline",
        state: "default",
        class: "border-b-gray-500 hover:border-b-gray-600 focus:border-b-gray-1000",
      },
    ],
    defaultVariants: {
      variant: "outline",
      size: "medium",
      state: "default",
    },
  },
);

const Control = <T,>({ size, variant, children, ...rest }: Props<T>) => {
  const { disabled, hasSelectedItems, selectedItems, open } = useSelectContext();

  const state = disabled ? "disabled" : hasSelectedItems ? "selected" : open ? "opened" : "default";

  return (
    <Select.Control {...rest}>
      <Select.Trigger asChild>
        <button type="button" className={controlVariants({ size, variant, state })}>
          {typeof children === "function" ? children({ selectedItems: selectedItems as T[] }) : children}

          <Select.Indicator>
            <Icon
              path={mdiChevronDown}
              size={size === "small" ? "14px" : size === "medium" ? "16px" : "18px"}
              className={clsx("absolute -translate-y-1/2 right-2 top-1/2", size === "small" ? "right-2" : "right-3")}
            />
          </Select.Indicator>
        </button>
      </Select.Trigger>
    </Select.Control>
  );
};

export default Control;
